import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  Save,
  Loader2,
  Image as ImageIcon,
  Camera,
  Package,
  Layers,
  Tag,
  Plus,
  Trash2,
} from 'lucide-react';
import { useDashboardTheme } from '../context/DashboardThemeContext';
import { supabase } from '../lib/supabase';
import { cn } from '@/lib/utils';

type Product = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  description_html: string | null;
  category_id: string | null;
  collection_id: string | null;
  price_xof: number | null;
  price_eur: number | null;
  base_price: number;
  is_active: boolean;
  is_featured: boolean;
  is_available_for_sale: boolean;
  sku: string | null;
};

type ProductImage = { id: string; url: string; alt_text: string | null; position: number; is_featured: boolean };
type ProductShot = { id: string; url: string; alt_text: string | null; caption: string | null; position: number };
type Option = { id: string; name: string; position: number; values: { id: string; value: string; position: number }[] };
type Variant = {
  id: string;
  title: string;
  sku: string | null;
  price: number;
  price_xof: number | null;
  price_eur: number | null;
  available_for_sale: boolean;
  quantity?: number;
  low_stock_threshold?: number;
};

type Category = { id: string; name: string };
type Collection = { id: string; name: string };

const BUCKET_PRODUCT_MEDIA = 'product-media';
const EUR_TO_XOF = 656;

export function DashboardProductEdit({ productId }: { productId: string }) {
  const { theme } = useDashboardTheme();
  const isLight = theme === 'light';
  const imageInputRef = useRef<HTMLInputElement>(null);
  const shotInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingShot, setUploadingShot] = useState(false);
  const [saving, setSaving] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [images, setImages] = useState<ProductImage[]>([]);
  const [shots, setShots] = useState<ProductShot[]>([]);
  const [options, setOptions] = useState<Option[]>([]);
  const [variants, setVariants] = useState<Variant[]>([]);

  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    description_html: '',
    category_id: '',
    collection_id: '',
    price_xof: '',
    price_eur: '',
    is_active: true,
    is_featured: false,
    is_available_for_sale: true,
    sku: '',
  });

  const load = useCallback(async () => {
    const [prodRes, imgRes, shotRes, optRes, catRes, colRes] = await Promise.all([
      supabase.from('products').select('*').eq('id', productId).single(),
      supabase.from('product_images').select('id, url, alt_text, position, is_featured').eq('product_id', productId).order('position'),
      supabase.from('product_shots').select('id, url, alt_text, caption, position').eq('product_id', productId).order('position'),
      supabase.from('product_options').select('id, name, position').eq('product_id', productId).order('position'),
      supabase.from('categories').select('id, name').order('name'),
      supabase.from('collections').select('id, name').order('name'),
    ]);

    if (prodRes.error || !prodRes.data) {
      setLoading(false);
      return;
    }

    const p = prodRes.data as Product;
    setProduct(p);
    setForm({
      name: p.name ?? '',
      slug: p.slug ?? '',
      description: p.description ?? '',
      description_html: p.description_html ?? '',
      category_id: p.category_id ?? '',
      collection_id: p.collection_id ?? '',
      price_xof: p.price_xof != null ? String(p.price_xof) : '',
      price_eur: p.price_eur != null ? String(p.price_eur) : '',
      is_active: p.is_active ?? true,
      is_featured: p.is_featured ?? false,
      is_available_for_sale: p.is_available_for_sale ?? true,
      sku: p.sku ?? '',
    });

    setImages((imgRes.data ?? []) as ProductImage[]);
    setShots((shotRes.data ?? []) as ProductShot[]);
    setCategories((catRes.data ?? []) as Category[]);
    setCollections((colRes.data ?? []) as Collection[]);

    const opts = (optRes.data ?? []) as { id: string; name: string; position: number }[];
    const optsWithValues: Option[] = [];
    for (const opt of opts) {
      const { data: vals } = await supabase
        .from('product_option_values')
        .select('id, value, position')
        .eq('option_id', opt.id)
        .order('position');
      optsWithValues.push({ ...opt, values: (vals ?? []) as { id: string; value: string; position: number }[] });
    }
    setOptions(optsWithValues);

    const varsRes = await supabase.from('product_variants').select('id, title, sku, price, price_xof, price_eur, available_for_sale').eq('product_id', productId);
    const vars = (varsRes.data ?? []) as Variant[];
    for (const v of vars) {
      const inv = await supabase.from('inventory').select('quantity, low_stock_threshold').eq('variant_id', v.id).single();
      if (inv.data) {
        v.quantity = inv.data.quantity;
        v.low_stock_threshold = inv.data.low_stock_threshold;
      }
    }
    setVariants(vars);
    setLoading(false);
  }, [productId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveProduct = async () => {
    if (!product) return;
    setSaving(true);
    await supabase
      .from('products')
      .update({
        name: form.name,
        slug: form.slug,
        description: form.description || null,
        description_html: form.description_html || null,
        category_id: form.category_id || null,
        collection_id: form.collection_id || null,
        price_xof: form.price_xof ? Number(form.price_xof) : null,
        price_eur: form.price_eur ? Number(form.price_eur) : null,
        is_active: form.is_active,
        is_featured: form.is_featured,
        is_available_for_sale: form.is_available_for_sale,
        sku: form.sku || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', productId);

    for (const v of variants) {
      await supabase.from('product_variants').update({
        price_xof: v.price_xof ?? undefined,
        price_eur: v.price_eur ?? undefined,
        available_for_sale: v.available_for_sale,
        updated_at: new Date().toISOString(),
      }).eq('id', v.id);
      const invRes = await supabase.from('inventory').select('id').eq('variant_id', v.id).single();
      if (invRes.data) {
        await supabase.from('inventory').update({
          quantity: v.quantity ?? 0,
          low_stock_threshold: v.low_stock_threshold ?? 5,
          updated_at: new Date().toISOString(),
        }).eq('variant_id', v.id);
      }
    }

    setSaving(false);
  };

  const triggerImagePick = () => imageInputRef.current?.click();
  const triggerShotPick = () => shotInputRef.current?.click();

  const uploadImageFile = async (file: File) => {
    setUploadingImage(true);
    const ext = file.name.replace(/^.*\./, '') || 'jpg';
    const path = `${productId}/images/${crypto.randomUUID()}-${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage.from(BUCKET_PRODUCT_MEDIA).upload(path, file, { contentType: file.type, upsert: false });
    if (upErr) {
      setUploadingImage(false);
      return;
    }
    const { data: urlData } = supabase.storage.from(BUCKET_PRODUCT_MEDIA).getPublicUrl(path);
    await supabase.from('product_images').insert({ product_id: productId, url: urlData.publicUrl, position: images.length });
    setUploadingImage(false);
    load();
  };

  const uploadShotFile = async (file: File) => {
    setUploadingShot(true);
    const ext = file.name.replace(/^.*\./, '') || 'jpg';
    const path = `${productId}/shots/${crypto.randomUUID()}-${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage.from(BUCKET_PRODUCT_MEDIA).upload(path, file, { contentType: file.type, upsert: false });
    if (upErr) {
      setUploadingShot(false);
      return;
    }
    const { data: urlData } = supabase.storage.from(BUCKET_PRODUCT_MEDIA).getPublicUrl(path);
    await supabase.from('product_shots').insert({ product_id: productId, url: urlData.publicUrl, position: shots.length });
    setUploadingShot(false);
    load();
  };

  const onImageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadImageFile(file);
    e.target.value = '';
  };

  const onShotInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadShotFile(file);
    e.target.value = '';
  };

  const deleteImage = (id: string) => {
    supabase.from('product_images').delete().eq('id', id).then(() => load());
  };

  const deleteShot = (id: string) => {
    supabase.from('product_shots').delete().eq('id', id).then(() => load());
  };

  const updateVariant = (variantId: string, updates: Partial<Variant>) => {
    setVariants((prev) => prev.map((v) => (v.id === variantId ? { ...v, ...updates } : v)));
  };

  const borderCls = isLight ? 'border-border/60' : 'border-white/10';
  const cardCls = isLight ? 'border-border/80 bg-card' : 'border-white/10 bg-white/5';

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="size-10 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-6 text-center">
        <p className="text-foreground">Produit introuvable.</p>
        <Link to="/espace-business/products" className="mt-4 inline-block text-sm underline">
          Retour à la liste
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-8 pb-12">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/espace-business/products"
            className={cn(
              'inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition-colors',
              isLight ? 'text-neutral-600 hover:bg-neutral-100' : 'text-white/70 hover:bg-white/10'
            )}
          >
            <ArrowLeft className="size-4" />
            Retour
          </Link>
          <h1 className="font-['Sora'] text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
            Modifier le produit
          </h1>
        </div>
        <Button className="gap-2 rounded-xl" onClick={saveProduct} disabled={saving}>
          {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          Enregistrer
        </Button>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Colonne gauche: Infos + Prix */}
        <div className="space-y-6">
          <Card className={cn('overflow-hidden rounded-2xl', cardCls)}>
            <CardHeader className={cn('border-b', borderCls)}>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <Package className="size-5" />
                Informations générales
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Nom, slug, description, catégorie et collection.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className={cn('flex flex-wrap items-center gap-6 rounded-xl border py-4 px-4 sm:gap-8', borderCls)}>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={form.is_active}
                    onCheckedChange={(checked) => setForm((f) => ({ ...f, is_active: checked === true }))}
                    aria-label="Actif"
                  />
                  <Label className="text-sm font-medium text-foreground cursor-pointer" onClick={() => setForm((f) => ({ ...f, is_active: !f.is_active }))}>Actif</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={form.is_featured}
                    onCheckedChange={(checked) => setForm((f) => ({ ...f, is_featured: checked === true }))}
                    aria-label="En vedette"
                  />
                  <Label className="text-sm font-medium text-foreground cursor-pointer" onClick={() => setForm((f) => ({ ...f, is_featured: !f.is_featured }))}>En vedette</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={form.is_available_for_sale}
                    onCheckedChange={(checked) => setForm((f) => ({ ...f, is_available_for_sale: checked === true }))}
                    aria-label="En vente"
                  />
                  <Label className="text-sm font-medium text-foreground cursor-pointer" onClick={() => setForm((f) => ({ ...f, is_available_for_sale: !f.is_available_for_sale }))}>En vente</Label>
                </div>
              </div>
              <div className="grid gap-2">
                <Label className="text-foreground">Nom</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className={cn('rounded-xl', isLight ? 'bg-background' : 'bg-white/5')}
                  placeholder="Nom du produit"
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-foreground">Slug (URL)</Label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                  className={cn('rounded-xl', isLight ? 'bg-background' : 'bg-white/5')}
                  placeholder="mon-produit"
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-foreground">Description</Label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={4}
                  className={cn('w-full rounded-xl border px-3 py-2 text-sm', borderCls, isLight ? 'bg-background' : 'bg-white/5')}
                  placeholder="Description du produit"
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label className="text-foreground">Catégorie</Label>
                  <Select
                    value={form.category_id || '__none__'}
                    onValueChange={(v) => setForm((f) => ({ ...f, category_id: v === '__none__' || !v ? '' : v }))}
                  >
                    <SelectTrigger className={cn('rounded-xl', borderCls)}>
                      <SelectValue placeholder="Catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Aucune</SelectItem>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label className="text-foreground">Collection</Label>
                  <Select
                    value={form.collection_id || '__none__'}
                    onValueChange={(v) => setForm((f) => ({ ...f, collection_id: v === '__none__' || !v ? '' : v }))}
                  >
                    <SelectTrigger className={cn('rounded-xl', borderCls)}>
                      <SelectValue placeholder="Collection" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Aucune</SelectItem>
                      {collections.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-2">
                <Label className="text-foreground">SKU</Label>
                <Input
                  value={form.sku}
                  onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))}
                  className={cn('rounded-xl', isLight ? 'bg-background' : 'bg-white/5')}
                  placeholder="Référence"
                />
              </div>
            </CardContent>
          </Card>

          <Card className={cn('overflow-hidden rounded-2xl', cardCls)}>
            <CardHeader className={cn('border-b', borderCls)}>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <Tag className="size-5" />
                Prix
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Prix du produit (F CFA et Euro).
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="mb-3 text-xs text-muted-foreground">Conversion automatique : 1 EUR = 656 F CFA</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label className="text-foreground">Prix (F CFA)</Label>
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    value={form.price_xof}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const xof = raw === '' ? '' : raw;
                      const eur = raw === '' ? '' : String((Number(raw) / EUR_TO_XOF).toFixed(2));
                      setForm((f) => ({ ...f, price_xof: xof, price_eur: eur }));
                    }}
                    className={cn('rounded-xl', isLight ? 'bg-background' : 'bg-white/5')}
                    placeholder="0"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-foreground">Prix (EUR)</Label>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={form.price_eur}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const eur = raw === '' ? '' : raw;
                      const xof = raw === '' ? '' : String(Math.round(Number(raw) * EUR_TO_XOF));
                      setForm((f) => ({ ...f, price_eur: eur, price_xof: xof }));
                    }}
                    className={cn('rounded-xl', isLight ? 'bg-background' : 'bg-white/5')}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Colonne droite: Images + Shots */}
        <div className="space-y-6">
          <Card className={cn('overflow-hidden rounded-2xl', cardCls)}>
            <CardHeader className={cn('border-b', borderCls)}>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <ImageIcon className="size-5" />
                Images produit
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Galerie de la fiche produit (fond neutre).
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {images.map((img) => (
                  <div key={img.id} className="group relative aspect-square overflow-hidden rounded-xl border bg-muted/30">
                    <img src={img.url} alt={img.alt_text ?? ''} className="h-full w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => deleteImage(img.id)}
                      className="absolute right-2 top-2 rounded-lg bg-black/60 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
                      aria-label="Supprimer l'image"
                      title="Supprimer l'image"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>
                ))}
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onImageInputChange}
                  aria-label="Choisir une image"
                />
                <button
                  type="button"
                  onClick={triggerImagePick}
                  disabled={uploadingImage}
                  className={cn(
                    'flex aspect-square items-center justify-center rounded-xl border border-dashed transition-colors',
                    borderCls,
                    isLight ? 'hover:bg-neutral-50' : 'hover:bg-white/5'
                  )}
                >
                  {uploadingImage ? <Loader2 className="size-8 animate-spin text-muted-foreground" /> : <Plus className="size-8 text-muted-foreground" />}
                </button>
              </div>
            </CardContent>
          </Card>

          <Card className={cn('overflow-hidden rounded-2xl', cardCls)}>
            <CardHeader className={cn('border-b', borderCls)}>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <Camera className="size-5" />
                Shots (mannequins)
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Photos du produit porté.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {shots.map((shot) => (
                  <div key={shot.id} className="group relative aspect-square overflow-hidden rounded-xl border bg-muted/30">
                    <img src={shot.url} alt={shot.alt_text ?? ''} className="h-full w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => deleteShot(shot.id)}
                      className="absolute right-2 top-2 rounded-lg bg-black/60 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
                      aria-label="Supprimer le shot"
                      title="Supprimer le shot"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>
                ))}
                <input
                  ref={shotInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onShotInputChange}
                  aria-label="Choisir une photo shot"
                />
                <button
                  type="button"
                  onClick={triggerShotPick}
                  disabled={uploadingShot}
                  className={cn(
                    'flex aspect-square items-center justify-center rounded-xl border border-dashed transition-colors',
                    borderCls,
                    isLight ? 'hover:bg-neutral-50' : 'hover:bg-white/5'
                  )}
                >
                  {uploadingShot ? <Loader2 className="size-8 animate-spin text-muted-foreground" /> : <Plus className="size-8 text-muted-foreground" />}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Options (lecture pour l’instant) */}
      <Card className={cn('overflow-hidden rounded-2xl', cardCls)}>
        <CardHeader className={cn('border-b', borderCls)}>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Layers className="size-5" />
            Options & variantes
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {options.length ? `${options.length} option(s), ${variants.length} variante(s).` : 'Aucune option. Les variantes sont listées ci-dessous.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {options.length > 0 && (
            <div className="mb-6 flex flex-wrap gap-4">
              {options.map((opt) => (
                <div key={opt.id} className="rounded-xl border px-4 py-2">
                  <span className="font-['Rubik'] text-sm font-medium text-foreground">{opt.name}</span>
                  <span className="ml-2 text-sm text-muted-foreground">
                    {opt.values.map((v) => v.value).join(', ')}
                  </span>
                </div>
              ))}
            </div>
          )}
          {variants.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className={cn('border-b', borderCls)}>
                    <th className="pb-2 pr-4 font-['Rubik'] text-xs uppercase text-muted-foreground">Variante</th>
                    <th className="pb-2 px-4 font-['Rubik'] text-xs uppercase text-muted-foreground">Prix XOF</th>
                    <th className="pb-2 px-4 font-['Rubik'] text-xs uppercase text-muted-foreground">Prix EUR</th>
                    <th className="pb-2 px-4 font-['Rubik'] text-xs uppercase text-muted-foreground">Stock</th>
                    <th className="pb-2 px-4 font-['Rubik'] text-xs uppercase text-muted-foreground">Seuil alerte</th>
                    <th className="pb-2 pl-4 font-['Rubik'] text-xs uppercase text-muted-foreground">En vente</th>
                  </tr>
                </thead>
                <tbody>
                  {variants.map((v) => (
                    <tr key={v.id} className={cn('border-b', borderCls)}>
                      <td className="py-3 pr-4 font-medium text-foreground">{v.title}</td>
                      <td className="py-3 px-4">
                        <Input
                          type="number"
                          min={0}
                          step={1}
                          value={v.price_xof ?? ''}
                          onChange={(e) => updateVariant(v.id, { price_xof: e.target.value ? Number(e.target.value) : null })}
                          className={cn('w-24 rounded-lg', isLight ? 'bg-background' : 'bg-white/5')}
                        />
                      </td>
                      <td className="py-3 px-4">
                        <Input
                          type="number"
                          min={0}
                          step={0.01}
                          value={v.price_eur ?? ''}
                          onChange={(e) => updateVariant(v.id, { price_eur: e.target.value ? Number(e.target.value) : null })}
                          className={cn('w-24 rounded-lg', isLight ? 'bg-background' : 'bg-white/5')}
                        />
                      </td>
                      <td className="py-3 px-4">
                        <Input
                          type="number"
                          min={0}
                          value={v.quantity ?? 0}
                          onChange={(e) => updateVariant(v.id, { quantity: Number(e.target.value) || 0 })}
                          className={cn('w-20 rounded-lg', isLight ? 'bg-background' : 'bg-white/5')}
                        />
                      </td>
                      <td className="py-3 px-4">
                        <Input
                          type="number"
                          min={0}
                          value={v.low_stock_threshold ?? 5}
                          onChange={(e) => updateVariant(v.id, { low_stock_threshold: Number(e.target.value) || 5 })}
                          className={cn('w-20 rounded-lg', isLight ? 'bg-background' : 'bg-white/5')}
                        />
                      </td>
                      <td className="py-3 pl-4">
                        <Checkbox
                          checked={v.available_for_sale}
                          onCheckedChange={(checked) => updateVariant(v.id, { available_for_sale: checked === true })}
                          aria-label={`${v.title} en vente`}
                          title="En vente"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Aucune variante. Ajoutez des options au produit pour générer des variantes.</p>
          )}
        </CardContent>
      </Card>

      {/* Réductions */}
      <Card className={cn('overflow-hidden rounded-2xl', cardCls)}>
        <CardHeader className={cn('border-b', borderCls)}>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Tag className="size-5" />
            Réductions & codes promo
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Les codes promo sont définis au niveau du site (table discount_codes) et peuvent s&apos;appliquer au catalogue, à une collection ou une catégorie.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}
